import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalShell } from './ModalShell';
import { RiskBanner } from './RiskBanner';
import { useToast } from './Toast';
import { Icons } from '@/shared/constants/icons';
import { usePauseDialogs } from '@/shared/stores/pause-dialogs.store';
import { getApiErrorMessage } from '@/shared/lib/api-errors';
import {
  useCanPausePublishing,
  usePausePublishing,
  usePublishingPause,
  useResumePublishing,
} from '@/shared/hooks/usePublishingPause';
import type { MissedPostsChoice } from '@/shared/services/publishing.service';

/**
 * Pause all publishing for a brand (Task 1.5): the confirm dialog, the
 * resume dialog that asks once about missed posts, the banner on Overview,
 * Calendar and Composer, the "Paused" pill by the brand switcher and the
 * overflow menu item. One dialog pair for the app, opened through
 * usePauseDialogs (shared/stores/pause-dialogs.store.ts).
 */

function PauseDialog(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data } = usePublishingPause();
  const pause = usePausePublishing();
  const close = usePauseDialogs((s) => s.close);
  const open = usePauseDialogs((s) => s.open === 'pause');
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  const confirm = async (): Promise<void> => {
    try {
      await pause.mutateAsync(reason.trim() || undefined);
      toast('success', t('pause.toast.paused'));
      close();
    } catch (err) {
      toast('error', getApiErrorMessage(err, t('pause.toast.failed')));
    }
  };

  return (
    <ModalShell open={open} onClose={close} labelledBy="pause-title">
      <div className="card w-[min(460px,calc(100vw-32px))] p-5 shadow-pop" data-testid="pause-dialog">
        <h2 id="pause-title" className="serif m-0 text-[22px] text-[var(--ink)]">{t('pause.dialog.title')}</h2>
        <ul className="mt-3 mb-0 ps-5 text-body text-[var(--ink-2)] space-y-1.5">
          <li>{t('pause.dialog.holds', { count: data?.held ?? 0 })}</li>
          <li>{t('pause.dialog.notInbox')}</li>
          <li>{t('pause.dialog.resume')}</li>
        </ul>
        <label htmlFor="pause-reason" className="block text-meta font-semibold text-[var(--ink-2)] mt-4 mb-1">{t('pause.dialog.reason')}</label>
        <input id="pause-reason" className="input" value={reason} maxLength={280} onChange={(e) => setReason(e.target.value)} placeholder={t('pause.dialog.reasonHint')} dir="auto" />
        <div className="flex flex-wrap gap-2 mt-5 justify-end">
          <button type="button" className="btn btn-secondary" onClick={close} disabled={pause.isPending}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-destructive" onClick={() => void confirm()} disabled={pause.isPending} aria-busy={pause.isPending || undefined}>
            <Icons.pause size={15} aria-hidden="true" />
            {pause.isPending ? t('attention.working') : t('pause.dialog.confirm')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ResumeDialog(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data } = usePublishingPause();
  const resume = useResumePublishing();
  const close = usePauseDialogs((s) => s.close);
  const open = usePauseDialogs((s) => s.open === 'resume');
  const missed = data?.missed ?? 0;

  const run = async (choice?: MissedPostsChoice): Promise<void> => {
    try {
      await resume.mutateAsync(choice);
      toast('success', choice === 'to_drafts' ? t('pause.toast.resumedDrafts', { count: missed }) : t('pause.toast.resumed'));
      close();
    } catch (err) {
      toast('error', getApiErrorMessage(err, t('pause.toast.failed')));
    }
  };

  return (
    <ModalShell open={open} onClose={close} labelledBy="resume-title">
      <div className="card w-[min(460px,calc(100vw-32px))] p-5 shadow-pop" data-testid="resume-dialog">
        <h2 id="resume-title" className="serif m-0 text-[22px] text-[var(--ink)]">
          {missed > 0 ? t('pause.resume.missedTitle', { count: missed }) : t('pause.resume.title')}
        </h2>
        <p className="m-0 mt-2 text-body text-[var(--ink-2)]">
          {missed > 0 ? t('pause.resume.missedBody') : t('pause.resume.body')}
        </p>
        <div className="flex flex-wrap gap-2 mt-5 justify-end">
          <button type="button" className="btn btn-ghost" onClick={close} disabled={resume.isPending}>{t('common.cancel')}</button>
          {missed > 0 ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => void run('to_drafts')} disabled={resume.isPending}>{t('pause.resume.toDrafts')}</button>
              <button type="button" className="btn btn-primary" onClick={() => void run('publish_now')} disabled={resume.isPending}>{t('pause.resume.publishNow', { count: missed })}</button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => void run()} disabled={resume.isPending}>{t('pause.resume.confirm')}</button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

/** The dialogs, once in the layout. */
export function PublishingPauseDialogs(): ReactElement {
  return (
    <>
      <PauseDialog />
      <ResumeDialog />
    </>
  );
}

/** "Publishing is paused · 6 posts held · Resume", at the top of Overview, Calendar and Composer. */
export function PublishingPausedBanner({ className = '' }: { className?: string }): ReactElement | null {
  const { t } = useTranslation();
  const { data } = usePublishingPause();
  const canManage = useCanPausePublishing();
  const show = usePauseDialogs((s) => s.show);
  if (!data?.paused) return null;
  return (
    <RiskBanner
      className={className}
      message={t('pause.banner', { count: data.held })}
      detail={data.reason ? <span dir="auto">{data.reason}</span> : undefined}
      action={canManage ? { label: t('pause.resume.button'), onClick: () => show('resume') } : undefined}
    />
  );
}

/** A small "Paused" pill next to the brand switcher. */
export function PausedPill(): ReactElement | null {
  const { t } = useTranslation();
  const { data } = usePublishingPause();
  if (!data?.paused) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-meta font-semibold shrink-0 bg-[var(--warn-soft)] text-[var(--warn)]"
      data-testid="paused-pill"
      title={t('pause.pillHint')}
    >
      <Icons.pause size={12} aria-hidden="true" />
      {t('pause.pill')}
    </span>
  );
}

/** An overflow menu with "Pause all publishing" (or "Resume publishing"), for owners and admins. */
export function PublishingOverflowMenu({ label }: { label: string }): ReactElement | null {
  const { t } = useTranslation();
  const { data } = usePublishingPause();
  const canManage = useCanPausePublishing();
  const show = usePauseDialogs((s) => s.show);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  if (!canManage) return null;
  const paused = data?.paused === true;
  return (
    <div ref={ref} className="relative">
      <button type="button" className="btn btn-ghost btn-icon-sm max-sm:w-11 max-sm:h-11" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((v) => !v)}>
        <Icons.more size={16} aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" aria-label={label} className="absolute end-0 top-full mt-1 z-30 min-w-[220px] rounded-xl border border-[var(--line)] bg-[var(--color-surface)] p-1 flex flex-col shadow-pop">
          <button
            type="button"
            role="menuitem"
            className="flex items-center gap-2 min-h-[40px] px-2.5 rounded-lg text-ui text-start bg-transparent border-none cursor-pointer hover:bg-[var(--color-surface-2)] text-[var(--ink)]"
            onClick={() => { setOpen(false); show(paused ? 'resume' : 'pause'); }}
          >
            {paused ? <Icons.play size={14} aria-hidden="true" /> : <Icons.pause size={14} aria-hidden="true" />}
            {paused ? t('pause.resume.button') : t('pause.menu')}
          </button>
        </div>
      )}
    </div>
  );
}
